import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { RootState } from '../../redux/rootReducer';
import { setUserInfo } from '../../redux/reducers/authSlice';
import UserService from '../../services/user.service';
import AuthInput from './AuthInput';
import AuthPrimaryButton from './AuthPrimaryButton';
import {
    modalBody,
    modalNotice,
    modalShell,
    modalSubtitle,
    modalTitle,
} from './modalDesign';

export default function ForcePasswordChangeModal() {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const dispatch = useDispatch();
    const userService = new UserService();

    const [passwordOld, setPasswordOld] = useState('');
    const [password1, setPassword1] = useState('');
    const [password2, setPassword2] = useState('');
    const [loading, setLoading] = useState(false);

    if (!userInfo?.mustResetPassword) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!passwordOld) {
            toast.error('Please enter the temporary password from your email');
            return;
        }
        if (password1.length < 5) {
            toast.error('New password must be at least 5 characters');
            return;
        }
        if (password1 !== password2) {
            toast.error('New passwords must match');
            return;
        }
        if (passwordOld === password1) {
            toast.error('Choose a new password different from the temporary one');
            return;
        }

        setLoading(true);
        try {
            const response = await userService.passwordUpdate({
                passwordOld,
                password1,
                password2,
            });
            setLoading(false);
            if (!response?.status) {
                toast.error(response?.message || 'Unable to update password');
                return;
            }
            dispatch(setUserInfo({ ...userInfo, mustResetPassword: false }));
            toast.success(response?.message || 'Password updated');
        } catch (err: any) {
            setLoading(false);
            toast.error(err?.message || 'Unable to update password');
        }
    };

    const modal = (
        <div className="fixed inset-0 z-[1300] overflow-y-auto bg-[#1B1530]/50 backdrop-blur-[2px]">
            <div className="flex min-h-[100dvh] items-center justify-center px-3 py-6">
                <div className={`${modalShell} w-full max-w-[min(100vw-1.5rem,28rem)]`}>
                    <div className={modalBody}>
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-ryd-primaryLess1 text-ryd-primary">
                            <Lock size={22} />
                        </div>
                        <h2 className={modalTitle}>Change your password</h2>
                        <p className={modalSubtitle}>
                            You signed in with a temporary password. Please set a new one to keep your account secure.
                        </p>
                        <p className={`${modalNotice} mt-4`}>
                            This step is required before you can continue using the dashboard.
                        </p>

                        <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
                            <AuthInput
                                label="Current password"
                                isPassword
                                placeholder="Temporary password from your email"
                                required
                                leftIcon={<Lock size={18} />}
                                value={passwordOld}
                                onChange={(e) => setPasswordOld(e.target.value)}
                                autoComplete="current-password"
                            />
                            <AuthInput
                                label="New password"
                                isPassword
                                placeholder="At least 5 characters"
                                required
                                minLength={5}
                                leftIcon={<Lock size={18} />}
                                value={password1}
                                onChange={(e) => setPassword1(e.target.value)}
                                autoComplete="new-password"
                            />
                            <AuthInput
                                label="Confirm new password"
                                isPassword
                                placeholder="Re-enter new password"
                                required
                                minLength={5}
                                leftIcon={<Lock size={18} />}
                                value={password2}
                                onChange={(e) => setPassword2(e.target.value)}
                                autoComplete="new-password"
                            />
                            <AuthPrimaryButton loading={loading} showArrow={false}>
                                Update password
                            </AuthPrimaryButton>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return modal;
    return createPortal(modal, document.body);
}
